import { getTrackerDisplayLabel } from "@/features/diaries/utils/displayLabels";
import { isActionableDueEntry } from "@/features/diaries/utils/findEarliestDueEntry";
import {
    compareTrackersBySortOrder,
    getSequenceBlockedEntry,
    getSequenceBlockedDueEntries,
    getSequenceBlockedReason,
    hasActionableDueEntry,
    hasSequenceBlockedDueEntry,
} from "@/features/diaries/utils/sequentialFormHelpers";
import type { StatefulTrackerViewT } from "@patient-watch/types";
import { useAuth } from "@/utils/auth";

export interface FormsToDoItem {
    id: string;
    name: string;
    is_due: boolean;
    is_locked?: boolean;
    end_date?: string | null;
    is_complete?: boolean;
    type: "tracker";
    data: StatefulTrackerViewT;
}

interface UseFormsToDoReturn {
    toDo: FormsToDoItem[];
    blocked: FormsToDoItem[];
    upToDate: FormsToDoItem[];
    upcoming: FormsToDoItem[];
    completed: FormsToDoItem[];
    itemCount: number;
    taskCount: number;
    isUpToDate: boolean;
}

/** Minimal auth shape needed by isFormRelevantForViewer / getFormsToDoCounts. */
export interface FormsAuthCtx {
    roles?: { isClinicianOnly?: boolean; isPatient?: boolean };
}

/** True if this tracker form is relevant to the current user's role. */
export function isFormRelevantForViewer(roles: string[] | null | undefined, auth: FormsAuthCtx): boolean {
    if (!roles?.length) {
        return auth.roles?.isPatient === true;
    }
    if (auth.roles?.isClinicianOnly) {
        return roles.includes("clinician");
    }
    if (auth.roles?.isPatient) {
        return roles.includes("patient");
    }
    return roles.includes("clinician") || roles.includes("patient");
}

function getDueEntriesCount(item: FormsToDoItem): number {
    return (
        (item.data.due_entries?.filter(isActionableDueEntry).length ?? 0) +
        getSequenceBlockedDueEntries(item.data).length
    );
}

function toFormsToDoItem(tracker: StatefulTrackerViewT): FormsToDoItem {
    return {
        id: tracker.tracker_id,
        name: getTrackerDisplayLabel(tracker),
        is_due: tracker.is_due === true,
        is_locked: tracker.next_entry?.is_locked,
        end_date: tracker.end_date,
        is_complete: tracker.tracker_complete,
        type: "tracker" as const,
        data: tracker,
    };
}

/** Pure counter (no React). Use this to sum across multiple diaries without nesting hooks. */
export function getFormsToDoCounts(
    trackers: StatefulTrackerViewT[] | null | undefined,
    auth: FormsAuthCtx
): { itemCount: number; taskCount: number } {
    let itemCount = 0;
    let taskCount = 0;

    for (const tracker of trackers ?? []) {
        if (!isFormRelevantForViewer((tracker as { roles?: string[] }).roles, auth)) continue;
        const dueEntriesLength =
            (tracker.due_entries?.filter(isActionableDueEntry).length ?? 0) +
            getSequenceBlockedDueEntries(tracker).length;
        if (dueEntriesLength > 0) {
            itemCount += 1;
            taskCount += dueEntriesLength;
        }
    }

    return { itemCount, taskCount };
}

export function partitionFormsToDo(
    trackers: StatefulTrackerViewT[] | null | undefined,
    auth: FormsAuthCtx
): UseFormsToDoReturn {
    const allItems = (trackers ?? [])
        .filter((tracker) => isFormRelevantForViewer((tracker as { roles?: string[] }).roles, auth))
        .map(toFormsToDoItem)
        .toSorted((a, b) => compareTrackersBySortOrder(a.data, b.data))
        .map((item, index) => ({ ...item, index: index + 1 }));

    const { itemCount, taskCount } = allItems.reduce(
        (acc, item) => {
            const dueEntriesCount = getDueEntriesCount(item);
            if (dueEntriesCount > 0) {
                acc.itemCount += 1;
                acc.taskCount += dueEntriesCount;
            }
            return acc;
        },
        { itemCount: 0, taskCount: 0 }
    );

    const toDo = allItems.filter((item) => hasActionableDueEntry(item.data));
    const blocked = allItems.filter(
        (item) => !hasActionableDueEntry(item.data) && hasSequenceBlockedDueEntry(item.data)
    );
    const upToDate = allItems.filter(
        (item) => !hasActionableDueEntry(item.data) && !hasSequenceBlockedDueEntry(item.data)
    );
    const completed = allItems.filter((item) => item.end_date !== null || item.is_complete === true);

    return {
        toDo,
        blocked,
        upToDate,
        upcoming: upToDate,
        completed,
        itemCount,
        taskCount,
        isUpToDate: taskCount === 0,
    };
}

export function useFormsToDo(trackers: StatefulTrackerViewT[]): UseFormsToDoReturn {
    return partitionFormsToDo(trackers, useAuth());
}

export function getBlockedFormExplanation(item: FormsToDoItem): string | null {
    const blockedEntry = getSequenceBlockedEntry(item.data);
    return getSequenceBlockedReason(blockedEntry);
}
